import { get, getBlob } from "../http-client/client";
import { ApiResponse } from "../http-client/Response";

/** Admin usage & cost reporting (QUERI-821). Costs are raw USD; `sekPerUsd` is the rate to show them in SEK. */

export interface UsageTotals {
  costUsd: number;
  /** Portion of costUsd that is estimated (backfilled history) */
  estimatedCostUsd: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  calls: number;
  /** Calls whose model has no known price */
  unpricedCalls: number;
  activeOrgs: number;
  activeUsers: number;
}

export interface UsageBreakdownRow {
  key: string;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  calls: number;
}

export interface UsageTimeseriesPoint {
  /** YYYY-MM-DD (UTC) */
  date: string;
  feature: string;
  costUsd: number;
}

export interface OrgUsageSummary {
  /** null = usage not attributable to an organization */
  organizationId: string | null;
  organizationName: string;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  calls: number;
  activeUsers: number;
}

export interface UserUsageDetail {
  userId: string | null;
  email: string;
  firstName: string;
  lastName: string;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  calls: number;
}

export interface UsageSummaryResponse {
  totals: UsageTotals;
  timeseries: UsageTimeseriesPoint[];
  byFeature: UsageBreakdownRow[];
  byModel: UsageBreakdownRow[];
  organizations: OrgUsageSummary[];
  sekPerUsd: number;
  startDate: string;
  endDate: string;
}

export interface OrgUsageDetailResponse {
  organization: OrgUsageSummary;
  totals: UsageTotals;
  timeseries: UsageTimeseriesPoint[];
  byFeature: UsageBreakdownRow[];
  byModel: UsageBreakdownRow[];
  users: UserUsageDetail[];
  sekPerUsd: number;
  startDate: string;
  endDate: string;
}

const rangeQuery = (startDate?: string, endDate?: string) => {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  const query = params.toString();
  return query ? `?${query}` : '';
};

export const getUsageSummary = (
  startDate?: string,
  endDate?: string,
): Promise<ApiResponse<UsageSummaryResponse>> =>
  get<UsageSummaryResponse>(`/admin/usage${rangeQuery(startDate, endDate)}`, null);

/** orgId null returns usage that isn't attributed to any organization */
export const getOrgUsage = (
  orgId: string | null,
  startDate?: string,
  endDate?: string,
): Promise<ApiResponse<OrgUsageDetailResponse>> =>
  get<OrgUsageDetailResponse>(
    `/admin/usage/${orgId ?? 'unattributed'}${rangeQuery(startDate, endDate)}`,
    null,
  );

/** Authenticated CSV download (a plain window.open would lack the auth header) */
export const exportUsageCsv = (startDate?: string, endDate?: string): Promise<ApiResponse<Blob>> =>
  getBlob(`/admin/usage/export/csv${rangeQuery(startDate, endDate)}`);
